import { Alert, Button, Checkbox, Group, Modal, Radio, Stack, Text, Loader, Box } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  donateBotToEnterprise,
  previewBotDonation,
  type DonateBotStrategy,
  type DonateDatasetStrategy,
  type DonatePreviewResponse,
} from 'packages/app/apis/services/robots';
import { useAdminStore } from '@/store/admin';

interface DonateToEnterpriseModalProps {
  opened: boolean;
  onClose: () => void;
  botId: string;
  onDonated: () => void;
}

const DonateToEnterpriseModal = ({ opened, onClose, botId, onDonated }: DonateToEnterpriseModalProps) => {
  const { t } = useTranslation();
  const { companyName, companyId } = useAdminStore();

  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<DonatePreviewResponse | null>(null);
  const [botStrategy, setBotStrategy] = useState<DonateBotStrategy>('move');
  const [strategy, setStrategy] = useState<DonateDatasetStrategy>('not_transfer');
  const [clearCollaborators, setClearCollaborators] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isCopy = botStrategy === 'copy';
  const sharedDatasets = preview?.datasets.filter((d) => d.shared) ?? [];
  const hasSharedDataset = sharedDatasets.length > 0;
  const hasAnyDataset = (preview?.datasets.length ?? 0) > 0;
  const canSubmit = !submitting && !previewLoading;

  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreview(null);
    setBotStrategy('move');
    setStrategy('not_transfer');
    setClearCollaborators(false);
    previewBotDonation(botId)
      .then((res) => {
        if (cancelled) return;
        setPreview(res);
      })
      .catch((err: any) => {
        if (cancelled) return;
        showNotification({
          message: err?.message || t('donate.error.preview_failed'),
          color: 'red',
        });
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [opened, botId, t]);

  const handleBotStrategyChange = (value: DonateBotStrategy) => {
    setBotStrategy(value);
    // 'transfer'（搬走原知识库）在复制模式下不成立——原 bot 还要继续用它
    if (value === 'copy' && strategy === 'transfer') {
      setStrategy('clone_then_transfer');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await donateBotToEnterprise(botId, {
        target_company_id: companyId ? String(companyId) : undefined,
        dataset_strategy: strategy,
        bot_strategy: botStrategy,
        clear_collaborators: clearCollaborators,
      });
      showNotification({
        message: isCopy ? t('donate.success_copy') : t('donate.success'),
        color: 'green',
      });
      onClose();
      onDonated();
    } catch (err: any) {
      showNotification({
        message: err?.message || t('donate.error.failed'),
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={submitting ? () => undefined : onClose}
      title={t('donate.modal.title', { company: companyName || '-' })}
      centered
      size="lg"
      withCloseButton={!submitting}
    >
      <Stack gap="md">
        {isCopy ? (
          <Alert variant="light" color="blue">
            {t('donate.modal.warning_copy')}
          </Alert>
        ) : (
          <Alert variant="light" color="yellow">
            {t('donate.modal.warning_irreversible')}
          </Alert>
        )}

        {previewLoading ? (
          <Group justify="center" py="md">
            <Loader size="sm" />
          </Group>
        ) : (
          <>
            {preview?.already_in_organization && (
              <Alert variant="light" color="red">
                {t('donate.error.already_in_org')}
              </Alert>
            )}

            <Box>
              <Text fw={600} mb={6}>
                {t('donate.modal.bot_strategy.label')}
              </Text>
              <Radio.Group value={botStrategy} onChange={(v) => handleBotStrategyChange(v as DonateBotStrategy)}>
                <Stack gap="xs">
                  <Radio value="move" label={t('donate.modal.bot_strategy.move')} />
                  <Radio value="copy" label={t('donate.modal.bot_strategy.copy')} />
                </Stack>
              </Radio.Group>
            </Box>

            {hasAnyDataset && !isCopy && (
              <Box>
                <Text fw={600} mb={6}>
                  {t('donate.modal.dataset_strategy.label')}
                </Text>
                <Radio.Group value={strategy} onChange={(v) => setStrategy(v as DonateDatasetStrategy)}>
                  <Stack gap="xs">
                    <Radio value="not_transfer" label={t('donate.modal.dataset_strategy.not_transfer')} />
                    <Radio
                      value="transfer"
                      disabled={hasSharedDataset}
                      label={t('donate.modal.dataset_strategy.transfer')}
                    />
                    {hasSharedDataset && (
                      <Text c="dimmed" size="xs" ml="lg">
                        {t('donate.modal.dataset_strategy.transfer_warning', {
                          datasets: sharedDatasets
                            .map((d) => `${d.dataset_name} (${d.linked_bot_names.join(', ')})`)
                            .join('; '),
                        })}
                      </Text>
                    )}
                    <Radio value="clone_then_transfer" label={t('donate.modal.dataset_strategy.clone_then_transfer')} />
                  </Stack>
                </Radio.Group>
              </Box>
            )}

            {hasAnyDataset && isCopy && (
              <Box>
                <Text fw={600} mb={6}>
                  {t('donate.modal.dataset_copy.label')}
                </Text>
                <Radio.Group value={strategy} onChange={(v) => setStrategy(v as DonateDatasetStrategy)}>
                  <Stack gap="xs">
                    <Radio value="clone_then_transfer" label={t('donate.modal.dataset_copy.clone')} />
                    <Radio value="not_transfer" label={t('donate.modal.dataset_copy.skip')} />
                  </Stack>
                </Radio.Group>
              </Box>
            )}

            {!isCopy && (
              <Checkbox
                label={t('donate.modal.clear_collaborators')}
                checked={clearCollaborators}
                onChange={(e) => setClearCollaborators(e.currentTarget.checked)}
                color="violet"
              />
            )}
          </>
        )}

        <Group justify="flex-end" mt={8}>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('donate.modal.cancel')}
          </Button>
          <Button
            color={isCopy ? 'violet' : 'red'}
            onClick={handleSubmit}
            loading={submitting}
            disabled={!canSubmit || preview?.already_in_organization}
          >
            {isCopy ? t('donate.modal.submit_copy') : t('donate.modal.submit')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default DonateToEnterpriseModal;
